import React, { useContext, useRef, useState } from 'react';
import { Box } from '@mui/material';
import ChatBoxHeading from './ChatBoxHeading';
import ChatArea from './ChatArea';
import ChatInputArea from './ChatInputArea';
import isMobile from '../../hooks/isMobile';
import { MessageData, RoomData } from '../../store/constants/constants';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { SocketContext } from '../../utils/SocketProvider';
import {
  replyMessageAction,
  sendMessageAction,
  sendAttachmentAction,
  editMessageAction,
} from '../../store/message/messageActions';
import {
  clearSearchResults,
  setCurrentMatchIndex,
  setDebouncedSearchQuery,
  setIsSearchContextActive,
  setIsSearching,
  setSearchStatus,
  setSelectedMessage,
} from '../../store/message/messageSlice';
import { setSelectedChat } from '../../store/room/roomSlice';
import Welcome from './Welcome';

interface ChatBoxProps {
  chat: RoomData | null;
}

const ChatBox: React.FC<ChatBoxProps> = ({ chat }) => {
  const mobileView = isMobile();
  const dispatch: AppDispatch = useDispatch();
  const socket = useContext(SocketContext);
  const selectedMessage = useSelector((state: RootState) => state.message.selectedMessage);
  const searchStatus = useSelector((state: RootState) => state.message.searchStatus);
  const action = useSelector((state: RootState) => state.message.action);
  const [droppedFiles, setDroppedFiles] = useState<FileList | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleFileDrop = (files: FileList) => {
    setDroppedFiles(files); // Store dropped files
  };

  const [showPinnedContext, setShowPinnedContext] = useState(false);

  const addMessage = (message: MessageData) => {
    if (socket) {
      if (selectedMessage && selectedMessage?.id && action === 'reply') {
        // Reply message handling
        const updatedMessage = {
          ...message,
          messageId: selectedMessage?.id,
        };
        dispatch(
          replyMessageAction({
            socket,
            reply: updatedMessage,
          }),
        );
        dispatch(setSelectedMessage(null));
      } else if (selectedMessage && selectedMessage?.id && action === 'edit') {
        const updatedMessage = {
          ...message,
          id: selectedMessage?.id,
        };
        dispatch(
          editMessageAction({
            socket,
            edit: updatedMessage,
          }),
        );
      } else {
        // Regular message handling
        dispatch(
          sendMessageAction({
            socket,
            message: message,
          }),
        );
      }
    }
    // Update the message state
  };

  const addAttachment = (attachment: {
    base64String: string;
    fileName: string;
    extension: string;
    roomId: number;
  }) => {
    if (socket) {
      dispatch(
        sendAttachmentAction({
          socket,
          attachment: {
            ...attachment,
          },
        }),
      );
    }
  };

  const handleBackClick = () => {
    dispatch(setSelectedChat(null)); // Reset selected chat to null
  };

  if (!chat) {
    return <Welcome />;
  }

  // Toggle search bar visibility
  const handleSearchClick = () => {
    dispatch(setSearchStatus(!searchStatus));
  };

  const handleCloseSearch = () => {
    dispatch(setIsSearchContextActive(false));

    dispatch(setIsSearching(false));
    dispatch(setDebouncedSearchQuery(''));
    dispatch(setCurrentMatchIndex(0));
    dispatch(clearSearchResults());
    dispatch(setSearchStatus(false));
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  return (
    <Box
      width='100%'
      bgcolor='white'
      height={mobileView ? '100vh' : 'calc(100vh - 73px)'}
      display='flex'
      position='relative'
      alignItems='center'
      flexDirection='column'
      justifyContent='space-between'
      sx={{
        border: '1px solid lightgray',
        borderRadius: '5px',
      }}
    >
      <ChatBoxHeading
        chat={chat}
        onBackClick={handleBackClick}
        onCloseSearch={handleCloseSearch}
        onSearchClick={handleSearchClick}
        setShowPinnedContext={setShowPinnedContext}
      />

      {mobileView ? (
        <Box sx={{ flex: 1, width: '100%', overflowY: 'auto' }}>
          <ChatArea
            chat={chat}
            onFileDrop={handleFileDrop}
            showPinnedContext={showPinnedContext}
            setShowPinnedContext={setShowPinnedContext}
          />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            width: '100%',
            borderTop: '1px solid lightgray',
            // borderLeft: '2px solid lightgray',
            // borderRight: '2px solid lightgray',
            // borderRadius: '5px 5px 0 0',
            overflowY: 'auto',
          }}
        >
          {/* showSearch={showSearch} */}
          <ChatArea
            chat={chat}
            onFileDrop={handleFileDrop}
            showPinnedContext={showPinnedContext}
            setShowPinnedContext={setShowPinnedContext}
          />
        </Box>
      )}

      <ChatInputArea
        chat={chat}
        droppedFiles={droppedFiles}
        onSendMessage={addMessage}
        onSendAttachment={addAttachment}
      />
    </Box>
  );
};

export default ChatBox;
